import { Button } from "@components/design-system/button";
import { CalendarDays as CalendarIcon, ChevronDown } from "lucide-react";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import ReactDOM from "react-dom";

interface DatePickerProps {
  label: string;
  id?: string;
  value: string;
  defaultMonth?: string;
  onChange: (value: string) => void;
  error?: string;
  required?: boolean;
  placeholder?: string;
  readOnly?: boolean;
  minDate?: string; // YYYY-MM-DD format
  maxDate?: string; // YYYY-MM-DD format
  containerClassName?: string;
  className?: string;
  labelClassName?: string;
  showAbove?: boolean; // Force the calendar to show above the input
}

export function DatePicker({
  label,
  id,
  value,
  defaultMonth = "",
  onChange,
  error,
  required = false,
  placeholder = "Select date",
  readOnly = false,
  minDate,
  maxDate,
  containerClassName = "",
  className = "",
  labelClassName = "",
  showAbove = false,
}: DatePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());

  // Parse date string to Date object
  const parseDate = useCallback((dateString: string): Date => {
    if (!dateString) return new Date();

    const parts = dateString.split("-");
    if (parts.length === 3) {
      if (parts[0].length === 2) {
        // DD-MM-YYYY format
        const [day, month, year] = parts;
        return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      } else if (parts[0].length === 4) {
        // YYYY-MM-DD format
        return new Date(dateString);
      }
    }
    return new Date(dateString);
  }, []);

  // Initialize and update current month when value changes
  useEffect(() => {
    if (value) {
      setCurrentMonth(parseDate(value));
    } else if (defaultMonth) {
      setCurrentMonth(parseDate(defaultMonth));
    }
  }, [value, defaultMonth, parseDate]);
  const [showYearSelector, setShowYearSelector] = useState(false);
  const [showMonthSelector, setShowMonthSelector] = useState(false);
  const [shouldShowAbove, setShouldShowAbove] = useState(showAbove);
  const datePickerRef = useRef<HTMLDivElement>(null);
  const yearSelectorRef = useRef<HTMLDivElement>(null);
  const monthSelectorRef = useRef<HTMLDivElement>(null);
  const calendarRef = useRef<HTMLDivElement>(null);
  const [calendarStyle, setCalendarStyle] = useState<React.CSSProperties>({});

  // Generate a range of years (from current year - 100 to current year + 20)
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 121 }, (_, i) => currentYear - 100 + i);

  // Determine if calendar should show above based on position in viewport
  const checkPosition = useCallback(() => {
    if (!datePickerRef.current || !calendarRef.current) return;

    const inputRect = datePickerRef.current.getBoundingClientRect();
    const calendarHeight = calendarRef.current.offsetHeight;
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - inputRect.bottom;
    const spaceAbove = inputRect.top;

    // If explicitly set to show above, respect that setting
    if (showAbove) {
      setShouldShowAbove(true);
      return;
    }

    // If there's enough space below, show below
    if (spaceBelow >= 300) {
      // Minimum space needed for calendar
      setShouldShowAbove(false);
      return;
    }

    // If there's more space above than below, show above
    if (spaceAbove > spaceBelow && spaceAbove >= 300) {
      setShouldShowAbove(true);
      return;
    }

    // Default to below even if tight fit
    setShouldShowAbove(false);
  }, [showAbove]);

  // Check position when opening the calendar
  useEffect(() => {
    if (isOpen) {
      checkPosition();
    }
  }, [isOpen, checkPosition]);

  // Function to update calendar position
  const updateCalendarPosition = useCallback(() => {
    if (isOpen && datePickerRef.current) {
      const inputRect = datePickerRef.current.getBoundingClientRect();
      const width = Math.max(inputRect.width, 280); // Ensure minimum width for calendar

      let style: React.CSSProperties = {
        position: "fixed", // Use fixed positioning instead of absolute
        left: Math.round(inputRect.left),
        // width: Math.round(width),
        zIndex: 9999,
        backgroundColor: "white",
        borderRadius: "0.375rem",
        boxShadow:
          "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
        border: "1px solid #e5e7eb",
      };

      if (shouldShowAbove) {
        style.bottom = window.innerHeight - Math.round(inputRect.top);
      } else {
        style.top = Math.round(inputRect.bottom);
      }

      setCalendarStyle(style);
    }
  }, [isOpen, shouldShowAbove]);

  // Portal calendar positioning
  useLayoutEffect(() => {
    if (isOpen) {
      updateCalendarPosition();

      // Add scroll event listener to update position when scrolling
      window.addEventListener("scroll", updateCalendarPosition, true);
      window.addEventListener("resize", updateCalendarPosition);

      return () => {
        window.removeEventListener("scroll", updateCalendarPosition, true);
        window.removeEventListener("resize", updateCalendarPosition);
      };
    }
  }, [isOpen, updateCalendarPosition]);

  // Close calendar when clicking outside (support portal)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const inputEl = datePickerRef.current;
      const popupEl = calendarRef.current;
      if (
        inputEl &&
        !inputEl.contains(event.target as Node) &&
        popupEl &&
        !popupEl.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setShowYearSelector(false);
        setShowMonthSelector(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Format date as YYYY-MM-DD for input value
  const formatDateForInput = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Format date for display - converts to DD-MM-YYYY for UI display
  const formatDateForDisplay = (dateString: string): string => {
    if (!dateString) return "";

    try {
      // If already in DD-MM-YYYY format, return as is
      if (dateString.match(/^\d{2}-\d{2}-\d{4}$/)) {
        return dateString;
      }

      // Handle YYYY-MM-DD format
      if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [year, month, day] = dateString.split("-");
        return `${day}-${month}-${year}`;
      }

      // Handle other formats
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;

      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();

      return `${day}-${month}-${year}`;
    } catch (error) {
      return dateString;
    }
  };

  // Get days in month
  const getDaysInMonth = (year: number, month: number): number => {
    return new Date(year, month + 1, 0).getDate();
  };

  // Get day of week (0-6) for first day of month (adjusted for Monday as first day)
  const getFirstDayOfMonth = (year: number, month: number): number => {
    // Convert Sunday (0) to 6, and other days to day-1 (Monday=0, Tuesday=1, etc.)
    const day = new Date(year, month, 1).getDay();
    return day === 0 ? 6 : day - 1;
  };

  // Generate calendar days
  const generateCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();

    const daysInMonth = getDaysInMonth(year, month);
    const firstDayOfMonth = getFirstDayOfMonth(year, month);

    // Get days from previous month
    const daysInPrevMonth = getDaysInMonth(year, month - 1);
    const prevMonthDays = Array.from({ length: firstDayOfMonth }, (_, i) => ({
      day: daysInPrevMonth - firstDayOfMonth + i + 1,
      month: month - 1,
      year: month === 0 ? year - 1 : year,
      isCurrentMonth: false,
    }));

    // Current month days
    const currentMonthDays = Array.from({ length: daysInMonth }, (_, i) => ({
      day: i + 1,
      month,
      year,
      isCurrentMonth: true,
    }));

    // Calculate how many days we need from next month
    const totalDaysShown = 35; // 6 rows of 7 days
    const nextMonthDays = Array.from(
      {
        length:
          totalDaysShown - (prevMonthDays.length + currentMonthDays.length),
      },
      (_, i) => ({
        day: i + 1,
        month: month + 1,
        year: month === 11 ? year + 1 : year,
        isCurrentMonth: false,
      })
    );

    return [...prevMonthDays, ...currentMonthDays, ...nextMonthDays];
  };

  // Check if a date is within the allowed range
  const isDateInRange = (year: number, month: number, day: number): boolean => {
    const date = new Date(year, month, day);
    date.setHours(0, 0, 0, 0);

    let isAfterMinDate = true;
    let isBeforeMaxDate = true;

    if (minDate) {
      // Parse minDate based on format
      let minDateObj: Date;
      const parts = minDate.split("-");

      if (parts.length === 3) {
        if (parts[0].length === 2) {
          // DD-MM-YYYY format
          const [day, month, year] = parts;
          minDateObj = new Date(
            parseInt(year),
            parseInt(month) - 1,
            parseInt(day)
          );
        } else if (parts[0].length === 4) {
          // YYYY-MM-DD format
          const [year, month, day] = parts;
          minDateObj = new Date(
            parseInt(year),
            parseInt(month) - 1,
            parseInt(day)
          );
        } else {
          minDateObj = new Date(minDate);
        }
      } else {
        minDateObj = new Date(minDate);
      }

      minDateObj.setHours(0, 0, 0, 0);
      isAfterMinDate = date >= minDateObj;
    }

    if (maxDate) {
      // Parse maxDate based on format
      let maxDateObj: Date;
      const parts = maxDate.split("-");

      if (parts.length === 3) {
        if (parts[0].length === 2) {
          // DD-MM-YYYY format
          const [day, month, year] = parts;
          maxDateObj = new Date(
            parseInt(year),
            parseInt(month) - 1,
            parseInt(day)
          );
        } else if (parts[0].length === 4) {
          // YYYY-MM-DD format
          const [year, month, day] = parts;
          maxDateObj = new Date(
            parseInt(year),
            parseInt(month) - 1,
            parseInt(day)
          );
        } else {
          maxDateObj = new Date(maxDate);
        }
      } else {
        maxDateObj = new Date(maxDate);
      }

      maxDateObj.setHours(0, 0, 0, 0);
      isBeforeMaxDate = date <= maxDateObj;
    }

    return isAfterMinDate && isBeforeMaxDate;
  };

  // Handle date selection
  const handleDateSelect = (year: number, month: number, day: number) => {
    if (!isDateInRange(year, month, day)) return;

    // Update current month to the selected date
    setCurrentMonth(new Date(year, month, day));

    const selectedDate = new Date(year, month, day);
    onChange(formatDateForInput(selectedDate));
    // Don't close the calendar when a date is selected
  };

  // Check if a date is selected
  const isDateSelected = (
    year: number,
    month: number,
    day: number
  ): boolean => {
    if (!value) return false;

    // Parse the value based on format
    let selectedDate: Date;
    const parts = value.split("-");

    if (parts.length === 3) {
      if (parts[0].length === 2) {
        // DD-MM-YYYY format
        const [day, month, year] = parts;
        selectedDate = new Date(
          parseInt(year),
          parseInt(month) - 1,
          parseInt(day)
        );
      } else if (parts[0].length === 4) {
        // YYYY-MM-DD format
        const [year, month, day] = parts;
        selectedDate = new Date(
          parseInt(year),
          parseInt(month) - 1,
          parseInt(day)
        );
      } else {
        // Fallback
        selectedDate = new Date(value);
      }
    } else {
      // Fallback to direct parsing
      selectedDate = new Date(value);
    }

    return (
      selectedDate.getFullYear() === year &&
      selectedDate.getMonth() === month &&
      selectedDate.getDate() === day
    );
  };

  // Month names for display
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  // Handle year selection
  const handleYearChange = (year: number) => {
    // If a date is already selected, maintain the day, otherwise use the 1st day of month
    let day = 1;
    if (value) {
      const selectedDate = parseDate(value);
      day = selectedDate.getDate();
    }

    const newDate = new Date(year, currentMonth.getMonth(), day);
    setCurrentMonth(newDate);
    setShowYearSelector(false);

    // Trigger onChange event when year changes
    onChange(formatDateForInput(newDate));
  };

  // Handle month selection
  const handleMonthChange = (month: number) => {
    // If a date is already selected, maintain the day, otherwise use the 1st day of month
    let day = 1;
    if (value) {
      const selectedDate = parseDate(value);
      day = selectedDate.getDate();
    }

    const newDate = new Date(currentMonth.getFullYear(), month, day);
    setCurrentMonth(newDate);
    setShowMonthSelector(false);

    // Trigger onChange event when month changes
    onChange(formatDateForInput(newDate));
  };

  // Scroll to selected year when year selector opens
  useEffect(() => {
    if (showYearSelector && yearSelectorRef.current) {
      const selectedYearElement = yearSelectorRef.current.querySelector(
        `.year-${currentMonth.getFullYear()}`
      );
      if (selectedYearElement) {
        selectedYearElement.scrollIntoView({ block: "center" });
      }
    }
  }, [showYearSelector, currentMonth]);

  // Scroll to selected month when month selector opens
  useEffect(() => {
    if (showMonthSelector && monthSelectorRef.current) {
      const selectedMonthElement = monthSelectorRef.current.querySelector(
        `.month-${currentMonth.getMonth()}`
      );
      if (selectedMonthElement) {
        selectedMonthElement.scrollIntoView({ block: "center" });
      }
    }
  }, [showMonthSelector, currentMonth]);

  return (
    <div className="w-full flex flex-col space-y-2">
      {label && (
        <label htmlFor={id} className="text-[#2D3748] text-[16px] font-medium">
          {label}
          {required && <span className="text-[#ef4444] ml-1">*</span>}
        </label>
      )}
      <div className="relative sm:static" ref={datePickerRef}>
        <div
          className={`w-full h-[50px] px-[20px] py-[15px] border bg-white ${
            error ? "border-[#FF3B30]" : "border-[#E2E8F0]"
          } rounded-[15px] flex items-center justify-between  ${
            readOnly ? "" : "cursor-pointer"
          } ${className}`}
          onClick={() => {
            if (readOnly) return;

            // When opening the calendar, make sure we're showing the correct month/year
            if (!isOpen && value) {
              setCurrentMonth(parseDate(value));
            }

            setIsOpen(!isOpen);
          }}
        >
          <span
            className={`font-[500] text-[14px] ${
              value ? "text-[#1F1F1F]" : "text-[#A0AEC0]"
            } ${labelClassName}`}
          >
            {value ? formatDateForDisplay(value) : placeholder}
          </span>
          {!readOnly && (
            <div className="flex items-center">
              <CalendarIcon className="h-[20px] w-[20px]" />
            </div>
          )}
        </div>

        {isOpen &&
          typeof window !== "undefined" &&
          ReactDOM.createPortal(
            <div
              ref={calendarRef}
              className="bg-white w-[353px] border border-gray-200 shadow-lg rounded-lg z-50"
              style={calendarStyle}
            >
              <div className="p-4 flex flex-col gap-4">
                <div className="flex justify-between items-center mb-2">
                  <div className="flex items-center gap-1">
                    {/* Month selector */}
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => {
                          setShowMonthSelector(!showMonthSelector);
                          setShowYearSelector(false);
                        }}
                        className="w-[154px] h-[40px] flex items-center justify-between bg-[#F5FBFF] border-none rounded-md px-4 text-[#1F1F1F] font-bold focus:outline-none"
                      >
                        {monthNames[currentMonth.getMonth()]}
                        <ChevronDown className="h-4 w-4" />
                      </button>

                      {showMonthSelector && (
                        <div
                          ref={monthSelectorRef}
                          className="absolute z-50 mt-1 bg-white border border-gray-200 shadow-lg rounded max-h-48 overflow-y-auto w-full"
                        >
                          {monthNames.map((month, index) => (
                            <button
                              key={month}
                              type="button"
                              className={`w-full text-left px-2 py-1 text-sm cursor-pointer text-[#1F1F1F] font-medium month-${index} ${
                                index === currentMonth.getMonth()
                                  ? "bg-[#5C88C5] text-white"
                                  : "hover:bg-gray-100"
                              }`}
                              onClick={() => handleMonthChange(index)}
                            >
                              {month}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Year selector */}
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => {
                          setShowYearSelector(!showYearSelector);
                          setShowMonthSelector(false);
                        }}
                        className="w-[154px] h-[40px] flex items-center justify-between bg-[#F5FBFF] border-none rounded-md px-4 text-[#1F1F1F] font-bold focus:outline-none"
                      >
                        {currentMonth.getFullYear()}
                        <ChevronDown className="h-4 w-4" />
                      </button>

                      {showYearSelector && (
                        <div
                          ref={yearSelectorRef}
                          className="absolute z-50 mt-1 bg-white border border-gray-200 shadow-lg rounded max-h-48 overflow-y-auto w-full"
                        >
                          {years.map((year) => (
                            <button
                              key={year}
                              type="button"
                              className={`w-full text-left px-2 py-1 text-sm font-medium cursor-pointer year-${year} ${
                                year === currentMonth.getFullYear()
                                  ? "bg-[#5C88C5] text-white"
                                  : "hover:bg-gray-100"
                              }`}
                              onClick={() => handleYearChange(year)}
                            >
                              {year}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div>
                  <div className="grid grid-cols-7">
                    {["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"].map((day) => (
                      <div
                        key={day}
                        className="h-[32px] w-[45px] flex items-center justify-center text-[#5C88C5] font-medium text-sm"
                      >
                        {day}
                      </div>
                    ))}
                  </div>

                  <div className="grid grid-cols-7 gap-1 mt-1">
                    {generateCalendarDays().map((dateObj, index) => {
                      // For days not in current month, show them with reduced opacity
                      if (!dateObj.isCurrentMonth) {
                        return (
                          <div
                            key={`other-month-${index}`}
                            className="h-8 w-8"
                          ></div>
                        );
                      }

                      // For days outside allowed range
                      if (
                        !isDateInRange(dateObj.year, dateObj.month, dateObj.day)
                      ) {
                        return (
                          <div
                            key={`disabled-${index}`}
                            className="h-8 w-8 text-sm flex items-center justify-center text-gray-300"
                          >
                            {dateObj.day}
                          </div>
                        );
                      }

                      const today = new Date();
                      const isToday =
                        dateObj.day === today.getDate() &&
                        dateObj.month === today.getMonth() &&
                        dateObj.year === today.getFullYear();
                      const isSelected = isDateSelected(
                        dateObj.year,
                        dateObj.month,
                        dateObj.day
                      );

                      return (
                        <button
                          key={index}
                          type="button"
                          className={`
                            h-8 w-8 text-sm flex items-center justify-center 
                            ${
                              !isDateInRange(
                                dateObj.year,
                                dateObj.month,
                                dateObj.day
                              )
                                ? "text-gray-300 cursor-default"
                                : dateObj.isCurrentMonth
                                ? isSelected
                                  ? "bg-[#5C88C5] text-white cursor-pointer rounded-md"
                                  : isToday
                                  ? "border-2 border-[#5C88C5] text-[#5C88C5] font-bold cursor-pointer rounded-md"
                                  : "hover:bg-gray-100 text-black cursor-pointer"
                                : "text-black opacity-[38%] cursor-pointer"
                            }
                          `}
                          onClick={() =>
                            isDateInRange(
                              dateObj.year,
                              dateObj.month,
                              dateObj.day
                            ) &&
                            handleDateSelect(
                              dateObj.year,
                              dateObj.month,
                              dateObj.day
                            )
                          }
                        >
                          {dateObj.day}
                        </button>
                      );
                    })}
                  </div>
                </div>
                {/* Action buttons */}
                <div className="flex gap-2 items-center justify-center mb-3">
                  <Button
                    onClick={() => {
                      onChange("");
                      setIsOpen(false);
                    }}
                    variant="primary-outline"
                    className="w-[150px] h-[40px] text-[#1F1F1F] font-semibold"
                    disabled={!value} // Disable the reset button when no value is selected
                  >
                    Reset
                  </Button>
                  <Button
                    onClick={() => setIsOpen(false)}
                    className="w-[150px] h-[40px] bg-[#5C88C5] text-white font-semibold"
                  >
                    Done
                  </Button>
                </div>
              </div>
            </div>,
            document.body
          )}
      </div>
      {error && (
        <p className="mt-0 text-[12px] font-[500] text-[#FF3B30]">{error}</p>
      )}
    </div>
  );
}

export default DatePicker;
